import { useState, useEffect } from "react";
import {
  GraduationCap,
  DollarSign,
  BookOpen,
  TrendingUp,
  Clock,
  Calendar,
  Users,
  Menu,
  X,
} from "lucide-react";
import { Chart } from "react-google-charts";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Lottie from "lottie-react";
import Loading from "../../assets/Loading.json";
import moment from "moment";

function StatCard({ title, value, change, icon: Icon, trend }) {
  return (
    <div className="rounded-xl border bg-white shadow-md p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-800">{title}</h3>
        <Icon className="h-5 w-5 text-indigo-500" />
      </div>
      <div className="mt-2">
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p
          className={`text-xs ${
            trend === "up" ? "text-green-500" : "text-red-500"
          }`}
        >
          {change}
        </p>
      </div>
    </div>
  );
}

function Button({ children, onClick, variant = "primary", size = "default" }) {
  const baseStyle =
    "inline-flex items-center justify-center font-medium rounded-md transition focus:outline-none focus:ring-2 focus:ring-offset-2";
  const variants = {
    primary:
      "bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500",
    outline:
      "border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-indigo-500",
  };
  const sizes = {
    default: "px-4 py-2 text-sm",
    sm: "px-3 py-1 text-xs",
  };
  return (
    <button
      onClick={onClick}
      className={`${baseStyle} ${variants[variant]} ${sizes[size]}`}
    >
      {children}
    </button>
  );
}

function ScrollArea({ children }) {
  return <div className="relative overflow-auto max-h-[400px]">{children}</div>;
}

const CustomModal = ({ classes, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-md w-[90%] max-w-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Upcoming Classes</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-red-600"
          >
            ✕
          </button>
        </div>
        {classes.length > 0 ? (
          <ul className="space-y-3 max-h-[400px] overflow-y-auto">
            {classes.map((cls, idx) => (
              <li key={idx} className="border-b pb-2">
                <p>
                  <strong>Course:</strong> {cls.subject}
                </p>
                <p>
                  <strong>Teacher:</strong> {cls.teacher} ({cls.teacherUsername}
                  )({cls.teacherEmail})
                </p>
                <p>
                  <strong>Student:</strong> {cls.student} ({cls.studentUsername}
                  )({cls.studentEmail})
                </p>
                <p>
                  <strong>Date:</strong>{" "}
                  {moment(cls.date).format("D MMMM YYYY")}
                </p>
                <p>
                  <strong>Time:</strong> {cls.time}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <p>No upcoming classes.</p>
        )}
      </div>
    </div>
  );
};

// export default CustomModal;

// const studentGrowthData = [
//   ["Month", "Students"],
//   ["Jan", 1000],
//   ["Feb", 1170],
//   ["Mar", 1250],
//   ["Apr", 1530],
//   ["May", 1620],
// ];

const studentGrowthOptions = {
  title: "Student Growth Over Months",
  curveType: "function",
  legend: { position: "bottom" },
  // colors: ["#4F46E5"], // Indigo to match the theme
  colors: ["#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#6366F1"], // multiple colors
  hAxis: {
  format: 'MMM yyyy', // Jan 2025
},
};

const teacherGrowthOptions = {
  title: "Teacher Growth Over Months",
  curveType: "function",
  legend: { position: "bottom" },
  // colors: ["#4F46E5"], // Indigo to match the theme
  colors: ["#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#6366F1"], // multiple colors
  hAxis: {
  format: 'MMM yyyy', // Jan 2025
},
};

const courseGrowthOptions = {
  title: "Course Growth Over Months",
  curveType: "function",
  legend: { position: "bottom" },
  // colors: ["#4F46E5"], // Indigo to match the theme
  colors: ["#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#6366F1"], // multiple colors
  hAxis: {
  format: 'MMM yyyy', // Jan 2025
},
};

// const revenueOverviewData = [
//   ["Month", "Revenue"],
//   ["Jan", 2000],
//   ["Feb", 2500],
//   ["Mar", 3000],
//   ["Apr", 4000],
//   ["May", 4500],
// ];

const revenueOverviewOptions = {
  title: "Revenue Overview",
  curveType: "function",
  legend: { position: "bottom" },
  // colors: ["#4F46E5"], // Indigo to match the theme
  colors: ["#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#6366F1"], // multiple colors
  hAxis: {
  format: 'MMM yyyy', // Jan 2025
},
};

const chartTypes = [
  { label: "Line Chart", value: "LineChart" },
  { label: "Column Chart", value: "ColumnChart" },
  { label: "Bar Chart", value: "BarChart" },
  { label: "Area Chart", value: "AreaChart" },
  { label: "Combo Chart", value: "ComboChart" },
  { label: "Scatter Chart", value: "ScatterChart" }, // Note: needs different data
  { label: "Stepped Area Chart", value: "SteppedAreaChart" }, // Note: needs different data
  { label: "Histogram", value: "Histogram" }, // Note: needs different data
];

// const monthNames = [
//   "Jan",
//   "Feb",
//   "Mar",
//   "Apr",
//   "May",
//   "Jun",
//   "Jul",
//   "Aug",
//   "Sep",
//   "Oct",
//   "Nov",
//   "Dec",
// ];

// const formatGrowthData = (rawData) => {
//   const data = [["Month", "Students"]]; // ✅ Google Chart format
//   rawData.forEach((item) => {
//     const month = monthNames[item._id - 1] || "Unknown";
//     data.push([month, item.count]);
//   });
//   console.log("Formatted Growth Data:", data); // 🪵 Debug this
//   return data;
// };

// const formatRevenueData = (rawData) => {
//   const data = [["Month", "Revenue"]];
//   rawData.forEach((item) => {
//     const month = monthNames[item._id - 1];
//     data.push([month, item.revenue]);
//   });
//   return data;
// };

function AdminDashboard({
  setComponent,
  stats,
  loading,
  todaysClasses,
  upcomingClasses,
  studentGrowth,
  teacherGrowth,
  teacherGrowthData,
  setTeacherGrowthData,
  revenueOverview,
  activities,
  studentGrowthData,
  revenueOverviewData,
  setStudentGrowthData,
  setRevenueOverviewData,
  courseGrowthData,
  setCourseGrowthData,
  courseGrowth,
  setCourseGrowth,
}) {
  const navigate = useNavigate();

  const [showUpcomingModal, setShowUpcomingModal] = useState(false);
  const [selectedChart, setSelectedChart] = useState("LineChart");
  const [menuOpen, setMenuOpen] = useState(false);


  const handleChartChange = (e) => {
    setSelectedChart(e.target.value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-80">
          <Lottie animationData={Loading} loop={true} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold tracking-tight text-gray-800">
          Dashboard
        </h2>
        <div className="relative">
  {/* Large screen buttons */}
  <div className="hidden md:flex space-x-4">
    <button
      className="bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded"
      onClick={() => setComponent("TeacherApprovals")}
    >
      Teacher Approval Requests
    </button>
    <button
      className="bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded"
      onClick={() => setComponent("TeacherManagement")}
    >
      Manage Teachers
    </button>
    <button
      className="bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded"
      onClick={() => setComponent("StudentManagement")}
    >
      Manage Students
    </button>
    <button
      className="bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded"
      onClick={() => setComponent("WithdrawRequests")}
    >
      Withdraw Requests
    </button>
    <button
      className="bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded"
      onClick={() => setComponent("Transactions")}
    >
      Transactions
    </button>
  </div>

  {/* Mobile Hamburger Icon */}
  <div className="md:hidden">
    <button
      className="p-2 rounded border border-indigo-500 text-indigo-600"
      onClick={() => setMenuOpen(!menuOpen)}
    >
      {menuOpen ? <X size={24} /> : <Menu size={24} />}
    </button>

    {/* Dropdown Menu */}
    {menuOpen && (
      <div className="absolute z-10 mt-2 w-48 bg-white border rounded shadow-lg right-0">
        <button
          className="block w-full text-left px-4 py-2 hover:bg-indigo-50"
          onClick={() => {
            setComponent("TeacherApprovals");
            setMenuOpen(false);
          }}
        >
          Teacher Approval Requests
        </button>
        <button
          className="block w-full text-left px-4 py-2 hover:bg-indigo-50"
          onClick={() => {
            setComponent("TeacherManagement");
            setMenuOpen(false);
          }}
        >
          Manage Teachers
        </button>
        <button
          className="block w-full text-left px-4 py-2 hover:bg-indigo-50"
          onClick={() => {
            setComponent("StudentManagement");
            setMenuOpen(false);
          }}
        >
          Manage Students
        </button>
        <button
          className="block w-full text-left px-4 py-2 hover:bg-indigo-50"
          onClick={() => {
            setComponent("WithdrawRequests");
            setMenuOpen(false);
          }}
        >
         Withdraw Requests
        </button>
        <button
          className="block w-full text-left px-4 py-2 hover:bg-indigo-50"
          onClick={() => {
            setComponent("Transactions");
            setMenuOpen(false);
          }}
        >
          Transactions
        </button>
      </div>
    )}
  </div>
</div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Students"
          value={stats.totalStudents}
          // change="+12.5% from last month"
          icon={Users}
          // trend="up"
        />
        <StatCard
          title="Active Teachers"
          value={stats.totalTeachers}
          // change="+4.2% from last month"
          icon={GraduationCap}
          // trend="up"
        />
        <StatCard
          title="Total Revenue"
          value={`$ ${stats.totalRevenue}`}
          // change="+20.1% from last month"
          icon={DollarSign}
          // trend="up"
        />
        <StatCard
          title="Total Courses"
          value={stats.totalCourses}
          // change="+8.1% from last month"
          icon={BookOpen}
          // trend="up"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
        <div className="col-span-4 bg-white rounded-xl shadow-md">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-800">
              Today's Classes
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowUpcomingModal(true)}
            >
              View All Upcoming Classes
            </Button>
            {showUpcomingModal && (
              <CustomModal
                classes={upcomingClasses}
                onClose={() => setShowUpcomingModal(false)}
              />
            )}
          </div>
          {/* </div> */}
          <div className="p-4 space-y-4">
            <ScrollArea>
            {todaysClasses.length === 0 && (
              <div className="text-center text-gray-500 py-4">
                No classes scheduled for today.
              </div>
            )}
            
            {todaysClasses.map((cls, idx) => (
              <div
                key={cls.id}
                className="flex items-center justify-between border-b pb-4 last:border-0"
              >
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-800">
                    {cls.subject}
                  </p>
                  <p className="text-sm text-gray-500">{cls.teacher}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <Clock className="mr-1 h-4 w-4 text-indigo-500" />
                      {cls.time}
                    </span>
                    <span className="flex items-center">
                      <Calendar className="mr-1 h-4 w-4 text-indigo-500" />
                      {moment(cls.date).format("D MMMM YYYY")}
                    </span>
                  </div>
                </div>
                {/* <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-500">
                    {cls.students} students
                  </div>
                  <Button variant="outline" size="sm">
                    View Details
                  </Button>
                </div> */}
              </div>
            ))}
            </ScrollArea>
          </div>
        </div>
        <div className="col-span-3 bg-white rounded-xl shadow-md">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-800">
              Recent Activity
            </h3>
          </div>
          <ScrollArea>
            <div className="p-4 space-y-4">
              {activities.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-center justify-between border-b pb-4 last:border-0"
                >
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-800">
                      {activity.user}
                    </p>
                    <p className="text-sm text-gray-500">
                      {activity.action}{" "}
                      <span className="font-medium text-gray-800">
                        {activity.subject}
                      </span>
                    </p>
                  </div>
                  <div className="text-sm text-gray-500">{activity.time}</div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <label
          htmlFor="chartType"
          className="text-sm font-medium text-gray-700"
        >
          Select Chart Type:
        </label>
        <select
          id="chartType"
          value={selectedChart}
          onChange={handleChartChange}
          className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring focus:border-indigo-500"
        >
          {chartTypes.map((chart) => (
            <option key={chart.value} value={chart.value}>
              {chart.label}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="bg-white rounded-xl shadow-md">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-800">
              Student Growth
            </h3>
          </div>
          <div className="p-4 h-[200px]">
            <Chart
              chartType={selectedChart}
              width="100%"
              height="100%"
              data={studentGrowthData}
              options={studentGrowthOptions}
            />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-md">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-800">
              Revenue Overview
            </h3>
          </div>
          <div className="p-4 h-[200px]">
            <Chart
              chartType={selectedChart}
              width="100%"
              height="100%"
              data={revenueOverviewData}
              options={revenueOverviewOptions}
            />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-md">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-800">
              Teacher Growth
            </h3>
          </div>
          <div className="p-4 h-[200px]">
            <Chart
              chartType={selectedChart}
              width="100%"
              height="100%"
              data={teacherGrowthData}
              options={teacherGrowthOptions}
            />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-md">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-800">
              Course Growth
            </h3>
          </div>
          <div className="p-4 h-[200px]">
            <Chart
              chartType={selectedChart}
              width="100%"
              height="100%"
              data={courseGrowthData}
              options={courseGrowthOptions}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
